import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import LanguageSwitcher from "./LanguageSwitcher";
import { useI18n } from "@/i18n/I18nProvider";

export default function Header() {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { to: "/", label: t.nav.home, end: true },
    { to: "/services", label: t.nav.services },
    { to: "/about", label: t.nav.about },
    { to: "/contact", label: t.nav.contact },
  ];

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[hsl(var(--bg))]/90 backdrop-blur-md border-b border-[hsl(var(--line))]"
          : "bg-transparent"
      }`}
    >
      <div className="container-x flex items-center justify-between h-20 md:h-24">
        <Link to="/" data-testid="brand-link" className="flex items-baseline gap-2">
          <span
            className="display text-[hsl(var(--ink))] text-2xl md:text-[28px] tracking-[-0.02em]"
            style={{ fontFamily: "'Playfair Display', serif", fontWeight: 600 }}
          >
            ESRA
          </span>
          <span className="hidden sm:inline text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--muted-foreground))]">
            S.R.L.S.
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-10" data-testid="primary-nav">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={`nav-${l.to.replace("/", "") || "home"}`}
              className={({ isActive }) =>
                `text-[12px] uppercase tracking-[0.22em] font-medium link-underline ${
                  isActive ? "text-[hsl(var(--ink))]" : "text-[hsl(var(--ink-soft))]/80 hover:text-[hsl(var(--ink))]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-5 md:gap-7">
          <LanguageSwitcher variant="dark" />
          <Link
            to="/contact"
            data-testid="cta-quote-header"
            className="hidden md:inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] font-medium text-[hsl(var(--ink))] group"
          >
            <span className="link-underline">{t.nav.quote}</span>
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden p-2 -mr-2"
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          className="lg:hidden border-t border-[hsl(var(--line))] bg-[hsl(var(--bg))]"
          data-testid="mobile-menu"
        >
          <div className="container-x py-8 flex flex-col gap-5">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                className={({ isActive }) =>
                  `text-sm uppercase tracking-[0.22em] font-medium py-2 ${
                    isActive ? "text-[hsl(var(--ink))]" : "text-[hsl(var(--ink-soft))]/80"
                  }`
                }
                data-testid={`mobile-nav-${l.to.replace("/", "") || "home"}`}
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              className="btn-primary mt-4 w-fit"
              data-testid="mobile-cta-quote"
            >
              {t.nav.quote}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
